import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { IconButton } from '@grnhse/seedling/lib/birch/components/buttons';
import CancelButton from '@grnhse/seedling/lib/birch/components/forms/cancel_button';
import Form from '@grnhse/seedling/lib/birch/components/forms/form';
import SubmitButton from '@grnhse/seedling/lib/birch/components/forms/submit_button';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import NoIcon from '@grnhse/seedling/lib/birch/icons/no';
import YesIcon from '@grnhse/seedling/lib/birch/icons/yes';
import { CompactRow } from '@grnhse/seedling/lib/birch/layouts/compact_row';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import Popover from '@mui/material/Popover';
import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { withLocalePrefix, tList } from 'shared/utils/translation';
import TextAreaField from '@grnhse/seedling/lib/birch/components/forms/fields/text_area_field';
import CheckboxFieldGroup from './checkbox_field_group';
import { trackEvent } from '@grnhse/datacoral_tracker';
import Divider from '@grnhse/seedling/lib/birch/components/divider';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import { AiUserFeedbackMap } from '../../../candidate_profile_redesign/shared/types';
const t = withLocalePrefix('ai_feedback');

const PositiveFeedbackIcon = () => {
  return <YesIcon color={FullPalette.GREEN_700} />;
};

const NegativeFeedbackIcon = () => {
  return <NoIcon color={FullPalette.POPPY_600} />;
};

const Container = styled.div`
  padding: ${Tokens.spacing['3']};
`;

const Header = styled.div`
  margin-bottom: ${Tokens.spacing['2.5']};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${Tokens.spacing['1']};
  margin-top: ${Tokens.spacing['2.5']};
  justify-content: flex-end;
`;

type Sentiment = 'positive' | 'negative';

type FormData = {
  feedback: string[];
  comment?: string | null;
};

type FeedbackPopoverProps = {
  isOpen: boolean;
  sentiment: Sentiment;
  feedbackKey: string;
  onClose: () => void;
  children: React.ReactNode;
  onSubmit: (formData: FormData) => void;
  showComments?: boolean;
};

export const getEnFeedbackMap = (key: string): AiUserFeedbackMap => {
  const feedbackMap = {
    [key]: {
      positive_feedback: tList(
        `ai_feedback.${key}.positive_feedback`,
        undefined,
        true,
        'en'
      ),
      negative_feedback: tList(
        `ai_feedback.${key}.negative_feedback`,
        undefined,
        true,
        'en'
      ),
    },
  };

  return feedbackMap;
};

const FeedbackPopover = ({
  isOpen,
  sentiment,
  onClose,
  children,
  feedbackKey,
  onSubmit,
  showComments = false,
}: FeedbackPopoverProps) => {
  const anchorEl = useRef<HTMLDivElement | null>(null);
  const translatedFeedback = tList(
    `ai_feedback.${feedbackKey}.${sentiment}_feedback`
  );
  const feedbackOptions = translatedFeedback.map((translatedVal, idx) => {
    return { label: translatedVal, value: `${idx}` };
  });
  return (
    <>
      <span ref={anchorEl}>{children}</span>
      <Popover
        open={isOpen}
        anchorEl={anchorEl.current}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Container>
          <Header>
            <SecondaryBody medium>{t('header')}</SecondaryBody>
          </Header>
          <Stack spacing={1}>
            <Form onSubmit={onSubmit} defaultValues={{ feedback: [] }}>
              <Stack spacing={1}>
                <CheckboxFieldGroup name="feedback" options={feedbackOptions} />
                {showComments && (
                  <Grid sx={{ minWidth: '360px' }}>
                    <TextAreaField
                      name="comment"
                      ariaLabel={t('comments_placeholder')}
                      placeholder={t('comments_placeholder')}
                    />
                  </Grid>
                )}
              </Stack>
              <ButtonGroup>
                <CancelButton onClick={onClose}>{t('cancel')}</CancelButton>
                <SubmitButton>{t('submit')}</SubmitButton>
              </ButtonGroup>
            </Form>
          </Stack>
        </Container>
      </Popover>
    </>
  );
};

const fadeOut = keyframes`
  0% {opacity: 1;}
  100% {opacity: 0;}
`;

const Message = styled.div`
  .fade {
    animation: ${fadeOut} 1s forwards;
  }
`;

const AiUserFeedback = ({
  featureKey,
  trackerId,
  enUserFeedback,
  hideOnRender = false,
  divider = false,
  payload = {},
  labelMediumFont = true,
  showComments = false,
}: {
  featureKey: string;
  trackerId?: number | string;
  enUserFeedback: AiUserFeedbackMap;
  hideOnRender?: boolean;
  divider?: boolean;
  payload?: { [key: string]: unknown };
  labelMediumFont?: boolean;
  showComments?: boolean;
}) => {
  const [feedbackStatus, setFeedbackStatus] = useState<
    'hidden' | 'shown' | 'completed'
  >(hideOnRender ? 'hidden' : 'shown');
  const [isOpen, openPopover, closePopover] = useModal();
  const [fade, setFade] = useState(false);
  const [trackingIdentifier, setTrackingIdentifier] = useState('');
  const [popoverSentiment, setPopoverSentiment] =
    useState<Sentiment>('positive');

  useEffect(() => {
    if (trackerId) {
      const ids = JSON.parse(localStorage.getItem(featureKey) || '[]');
      if (ids.includes(trackerId)) {
        setFeedbackStatus('hidden');
      }
    }
    const handleSuggestionsGenerated = (event: CustomEvent) => {
      setTrackingIdentifier(event.detail);
      setFeedbackStatus('shown');
    };

    document.addEventListener(
      'suggestionsGenerated',
      handleSuggestionsGenerated as EventListener
    );
    return () =>
      document.removeEventListener(
        'suggestionsGenerated',
        handleSuggestionsGenerated as EventListener
      );
  }, []);

  useEffect(() => {
    if (feedbackStatus !== 'completed') {
      return;
    }
    //start fade out after 3.5 seconds
    //and hide after 5 seconds
    const timer = setTimeout(() => {
      setFade(true);
    }, 3500);
    const timer2 = setTimeout(() => {
      setFeedbackStatus('hidden');
    }, 5000);
    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
    };
  }, [feedbackStatus]);

  const handleSubmit = (formData: FormData) => {
    setFade(false);
    const formFeedbackInEnglish = formData.feedback.map((value: string) => {
      const idx = parseInt(value, 10);
      const options =
        enUserFeedback?.[featureKey]?.[`${popoverSentiment}_feedback`];
      return options?.[idx] ?? idx;
    });
    const eventPayload = {
      feedback: formFeedbackInEnglish,
      comment: formData.comment,
      sentiment: popoverSentiment,
      identifier: trackingIdentifier || trackerId,
      ...payload,
    };

    trackEvent(featureKey, eventPayload);
    setFeedbackStatus('completed');

    if (trackerId) {
      const ids = JSON.parse(localStorage.getItem(featureKey) || '[]');
      ids.push(trackerId);

      localStorage.setItem(featureKey, JSON.stringify(ids));
    }

    closePopover();
  };

  const handleFeedback = (sentiment: Sentiment) => {
    setPopoverSentiment(sentiment);
    openPopover();
  };

  if (feedbackStatus === 'hidden') {
    return null;
  }

  if (feedbackStatus === 'completed') {
    return (
      <Message>
        <SecondaryBody className={fade ? 'fade' : ''} metadata>
          {t('success')}
        </SecondaryBody>
      </Message>
    );
  }

  return (
    <Stack spacing={1} alignContent="flex-end">
      {divider && <Divider />}
      <CompactRow>
        <SecondaryBody medium={labelMediumFont}>
          {t(`${featureKey}.call_to_action`)}
        </SecondaryBody>

        <FeedbackPopover
          isOpen={isOpen}
          sentiment={popoverSentiment}
          feedbackKey={featureKey}
          onClose={closePopover}
          onSubmit={handleSubmit}
          showComments={showComments}
        >
          <CompactRow>
            <IconButton
              icon={PositiveFeedbackIcon}
              label={t('positive_icon_label')}
              onClick={() => handleFeedback('positive')}
            />
            <IconButton
              icon={NegativeFeedbackIcon}
              label={t('negative_icon_label')}
              onClick={() => handleFeedback('negative')}
            />
          </CompactRow>
        </FeedbackPopover>
      </CompactRow>
    </Stack>
  );
};

export default AiUserFeedback;
